import { createHOC } from 'vue-hoc';
import cloneDeep from 'lodash/cloneDeep';
import without from 'lodash/without';
import gql from 'graphql-tag';

import withUser from '~lib/common/hoc/withUser';
import auth from '~lib/common/providers/auth';
import casesService from '~lib/common/providers/casesService';
import { getAppLabOrg } from '~lib/common/config/appLabOrg';
import fecthOrgData from '~lib/common/utils/fetchOrgData';
import { isPublished, hasAppLabAccess } from '~lib/common/utils/publish';

import TemplateCases from './TemplateCases';
import defaultTemplatesAccessContext from '../../../utils/defaultTemplatesAccessContext';

const options = {
  name: 'TemplateCasesContainer',
  props: {
    user: {
      type: Object,
      required: true,
    },
    productType: {
      type: String,
      default: 'EMS'
    },
  },
  data() {
    return {
      state: {
        loading: false,
        updating: false,
        cases: [],
        sortBy: 'name',
        error: null,
        orgData: {
          users: [],
          groups: [],
        },
      },
    };
  },
  async created() {
    if (!(await auth.isAuthenticated())) {
      this.$router.replace({ name: 'login' });
      return;
    }
    fecthOrgData(this.user).then(orgData => {
      this.state.orgData = orgData;
    });
    await this.updateCases();
    this.sortCases(isPublished, false);
  },
  methods: {
    async updateCases() {
      try {
        this.state.loading = true;
        this.state.error = null;
        const response = await casesService.query({
          query: gql`
            query getCaseConfigurations($withAccess: AccessContextInput, $sortBy: String) {
              caseConfigurations(withAccess: $withAccess, sortBy: $sortBy) {
                id
                name
                tags {
                  text
                }
                dispatch {
                  learningObjectives {
                    caption {
                      en
                    }
                    displayed
                  }
                  chiefComplaint {
                    caption {
                      en
                    }
                    displayed
                  }
                }
                access {
                  owner
                  ownerOrg
                  read {
                    groups
                    users
                    roles
                  }
                  write {
                    groups
                    users
                    roles
                  }
                  run {
                    groups
                    users
                    roles
                  }
                  share {
                    groups
                    users
                    roles
                  }
                  productType
                }
                _avatar {
                  id
                  key
                  name {
                    en
                  }
                  picture {
                    url
                  }
                }
                _startLocation {
                  name {
                    en
                  }
                  picture {
                    url
                  }
                }
                _endLocation {
                  name {
                    en
                  }
                  picture {
                    url
                  }
                }
              }
            }
          `,
          variables: {
            withAccess: defaultTemplatesAccessContext[this.productType],
            sortBy: this.state.sortBy,
          },
          fetchPolicy: 'network-only',
        });
        this.state.cases = cloneDeep(response.data.caseConfigurations);
      } catch (e) {
        console.error(e);
        if (e.graphqlErrors && e.graphqlErrors.length) {
          this.state.error = e.graphqlErrors[0];
        }
        this.state.error = e;
      } finally {
        this.state.loading = false;
      }
    },
    async addGroups(selectedCase, groupsToAdd) {
      const selectedCaseClone = cloneDeep(selectedCase);

      if (selectedCaseClone.access.read) {
        selectedCaseClone.access.read.groups = (
          selectedCaseClone.access.read.groups || []
        ).concat(groupsToAdd);
      } else {
        selectedCaseClone.access.read = {
          groups: groupsToAdd
        };
      }

      if (selectedCaseClone.access.run) {
        selectedCaseClone.access.run.groups = (
          selectedCaseClone.access.run.groups || []
        ).concat(groupsToAdd);
      } else {
        selectedCaseClone.access.run = {
          groups: groupsToAdd
        };
      }
      this.updateGroups(selectedCase, selectedCaseClone);
    },
    removeGroups(selectedCase, groupsToRemove) {
      const selectedCaseClone = cloneDeep(selectedCase);
      selectedCaseClone.access.read.groups = without(
        selectedCaseClone.access.read.groups || [],
        ...groupsToRemove
      );
      selectedCaseClone.access.run.groups = without(
        selectedCaseClone.access.read.groups || [],
        ...groupsToRemove
      );
      this.updateGroups(selectedCase, selectedCaseClone);
    },
    async updateGroups(selectedCase, selectedCaseClone) {
      try {
        this.state.updating = true;
        this.state.error = null;
        await casesService.mutate({
          mutation: gql`
            mutation upsertCaseConfiguration(
              $caseConf: CaseConfigurationInput
            ) {
              upsertCaseConfiguration(caseConfiguration: $caseConf) {
                id
              }
            }
          `,
          variables: {
            caseConf: {
              id: selectedCaseClone.id,
              access: selectedCaseClone.access,
              isTemplate: true,
            },
            withAccess: defaultTemplatesAccessContext,
          },
        });
        const index = this.state.cases.indexOf(selectedCase);
        this.state.cases.splice(index, 1, selectedCaseClone);
      } catch (e) {
        console.error(e);
        if (e.graphqlErrors && e.graphqlErrors.length) {
          this.state.error = e.graphqlErrors[0];
        }
        this.state.error = e;
      } finally {
        this.state.updating = false;
      }
    },
    sortCases(func, descending) {
      this.state.cases = this.state.cases.sort((a, b) => {
        const isA = func(a);
        const isB = func(b);
        let val = 0;
        if (isA && !isB) {
           val = descending ? 1 : -1;
        }
        if (!isA && isB) {
          val = descending ? -1 : 1;
        }
        return val;
      });
    },
  },
};

const renderWith = {
  props: {
    loading() {
      return this.state.loading;
    },
    updating() {
      return this.state.updating;
    },
    cases() {
      return this.state.cases;
    },
    error() {
      return this.state.error;
    },
    orgData() {
      return this.state.orgData;
    },
  },
  listeners: {
    async publish(selectedCase) {
      this.addGroups(selectedCase, ['ALL_ADMINS', 'ALL_INSTRUCTORS']);
    },
    async unpublish(selectedCase) {
      this.removeGroups(selectedCase, ['ALL_ADMINS', 'ALL_INSTRUCTORS']);
    },
    async addAppLabAccess(selectedCase) {
      const appLabOrg = getAppLabOrg(this.productType);
      this.addGroups(selectedCase, [appLabOrg]);
    },
    async removeAppLabAccess(selectedCase) {
      const appLabOrg = getAppLabOrg(this.productType);
      this.removeGroups(selectedCase, [appLabOrg]);
    },
    sort(sortBy) {
      const descending = sortBy && sortBy[0] == '-';
      const fieldOnly = descending ? sortBy.slice(1) : sortBy;
      if (fieldOnly === 'published') {
        this.sortCases(isPublished, descending);
      } else if (fieldOnly === 'applab') {
        this.sortCases(hasAppLabAccess, descending);
      } else {
        this.state.sortBy = sortBy;
        this.updateCases();
      }
    },
  },
};

const TemplateCasesContainer = createHOC(TemplateCases, options, renderWith);
export default withUser()(TemplateCasesContainer);
